import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Coins, Loader2, History } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_GRANT = 5;

export function GrantCreditsDialog({ open, onOpenChange, authConfig }) {
  const [mobile, setMobile] = useState("");
  const [amount, setAmount] = useState(1);
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [balance, setBalance] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const reset = () => { setMobile(""); setAmount(1); setReason(""); setBalance(null); setHistory([]); };

  const loadHistory = async (m) => {
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${API}/players/wallet/grants?mobile=${encodeURIComponent(m)}`, authConfig());
      setHistory(res.data || []);
    } catch { setHistory([]); }
    setHistoryLoading(false);
  };

  const checkBalance = async () => {
    const clean = mobile.replace(/\s+/g, "");
    if (clean.length < 6) return;
    try {
      const res = await axios.get(`${API}/players/wallet?mobile=${encodeURIComponent(clean)}`);
      setBalance(res.data.credits);
      loadHistory(clean);
    } catch { setBalance(null); }
  };

  const submit = async () => {
    const clean = mobile.replace(/\s+/g, "");
    if (!clean || amount < 1) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/players/wallet/grant`, {
        mobile: clean, amount: Number(amount), reason: reason.trim(),
      }, authConfig());
      toast.success(`Granted ${amount} credit${amount === 1 ? "" : "s"} — ${clean} now has ${res.data.credits}.`);
      setBalance(res.data.credits);
      setReason("");
      loadHistory(clean);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't grant credits.");
    }
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="bg-[#141414] border-white/10 rounded-none max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-heading text-white text-xl flex items-center gap-2">
            <Coins className="h-5 w-5 text-[#E3292E]" /> Grant Player Credits
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-white/50 text-sm font-body">
            Comp free credits onto a player's wallet — for a technical glitch, a goodwill gesture, or a promo.
            Every grant is logged. Max {MAX_GRANT} credits per action.
          </p>
          <div className="space-y-3">
            <Input
              value={mobile}
              onChange={(e) => { setMobile(e.target.value); setBalance(null); setHistory([]); }}
              onBlur={checkBalance}
              type="tel"
              placeholder="Player mobile number"
              className="bg-transparent border-white/20 text-white rounded-none focus-visible:ring-1 focus-visible:ring-[#E3292E]"
              data-testid="grant-credits-mobile-input"
            />
            {balance !== null && (
              <p className="text-white/40 text-xs font-body">Current balance: <span className="text-white font-bold">{balance} credit{balance === 1 ? "" : "s"}</span></p>
            )}
            <Input
              value={amount}
              onChange={(e) => setAmount(Math.max(1, Math.min(MAX_GRANT, Number(e.target.value) || 1)))}
              type="number"
              min={1}
              max={MAX_GRANT}
              placeholder="Amount"
              className="bg-transparent border-white/20 text-white rounded-none focus-visible:ring-1 focus-visible:ring-[#E3292E]"
              data-testid="grant-credits-amount-input"
            />
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Reason (optional, but recommended)"
              maxLength={200}
              className="bg-transparent border-white/20 text-white rounded-none focus-visible:ring-1 focus-visible:ring-[#E3292E]"
              data-testid="grant-credits-reason-input"
            />
          </div>
          <Button
            onClick={submit}
            disabled={loading || !mobile.replace(/\s+/g, "") || amount < 1}
            className="w-full bg-[#E3292E] hover:bg-[#DC2626] text-white rounded-none font-heading font-bold"
            data-testid="grant-credits-submit-btn"
          >
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Coins className="mr-2 h-4 w-4" />}
            GRANT {amount} CREDIT{amount === 1 ? "" : "S"}
          </Button>

          {(historyLoading || history.length > 0) && (
            <div className="border-t border-white/10 pt-3">
              <p className="text-white/40 text-xs uppercase tracking-[0.15em] font-heading mb-2 flex items-center gap-1.5">
                <History className="h-3 w-3" /> Grant History
              </p>
              {historyLoading ? (
                <Loader2 className="h-4 w-4 text-white/30 animate-spin" />
              ) : (
                <div className="space-y-1.5 max-h-32 overflow-y-auto">
                  {history.map((g) => (
                    <div key={g.id} className="text-xs font-body text-white/50 flex justify-between gap-2">
                      <span className="truncate">{g.reason || "No reason given"} — <span className="text-white/30">{g.granted_by_username}</span></span>
                      <span className="text-[#309F6A] font-bold flex-shrink-0">+{g.amount}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
